import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, ClipboardCopy } from "@fleet/icons";
import { buildDiagnosticsReport, type DiagnosticSection } from "@mealplan/shared";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Copies a pasteable diagnostic report to the clipboard.
 *
 * The component only COLLECTS data and delegates formatting to
 * `buildDiagnosticsReport` (shared, a pure and tested function). If the report were
 * assembled here, every app would have its own version and they would diverge as soon as
 * someone added a field.
 *
 * It receives the SAME section array that the screen renders. It previously received its
 * own object while the screen rendered its rows itself: two
 * sources for the same data, so adding a row above left the report
 * incomplete without anyone noticing.
 *
 * Labels: `Copy` and `Copied!` are used, which are already translated into the twelve
 * languages. A new string like "Copy diagnostics" would appear in ENGLISH in all
 * of them without the i18n gate complaining, because it only validates what is already in
 * the catalog.
 */
/** How long "Copied!" remains before returning to the normal label. */
const CONFIRMATION_MS = 2000;

export function CopyDiagnosticsButton(props: {
  appName: string;
  /** The SAME model that the screen renders. */
  sections?: () => DiagnosticSection[];
  secciones?: () => DiagnosticSection[];
}) {
  const { t } = useI18n();
  const currentSections = () => props.sections?.() ?? props.secciones?.() ?? [];
  const [copied, setCopied] = createSignal(false);
  const [error, setError] = createSignal(false);

  const handleCopy = async () => {
    const texto = buildDiagnosticsReport({
      appName: props.appName,
      timestamp: new Date().toISOString(),
      sections: currentSections(),
    });
    try {
      await navigator.clipboard.writeText(texto);
      setError(false);
      setCopied(true);
      setTimeout(() => setCopied(false), CONFIRMATION_MS);
    } catch {
      // The clipboard fails without permission or outside a secure context. Ignoring it
      // silent would leave the user believing it copied: flag the failure and leave
      // the report selectable as plan B.
      setError(true);
    }
  };

  return (
    <div class="space-y-2">
      <Button
        variant="outline"
        class="w-full gap-2"
        data-testid="copy-diagnostics"
        onClick={() => void handleCopy()}
      >
        <Show when={copied()} fallback={<ClipboardCopy size={18} />}>
          <Check size={18} class="text-success" />
        </Show>
        {copied() ? t(msg`Copied!`) : t(msg`Copy`)}
      </Button>

      {/* Plan B cuando el portapapeles no está disponible: el informe a la vista
          para seleccionarlo a mano. Sin esto, el botón sería un callejón sin
          salida justo en los entornos donde más falta hace depurar. */}
      <Show when={error()}>
        <textarea
          data-testid="diagnostics-fallback"
          class="h-40 w-full rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          readonly
          onFocus={(e) => e.currentTarget.select()}
        >
          {buildDiagnosticsReport({
            appName: props.appName,
            timestamp: new Date().toISOString(),
            sections: currentSections(),
          })}
        </textarea>
      </Show>
    </div>
  );
}
